import React, { useState, useEffect } from "react";
import { Table, Input, Button, Space, Popconfirm, Typography, message, Col, Tooltip } from "antd";
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined, SearchOutlined } from "@ant-design/icons";
import ProductTagsServ from "../../service/ProductTagServ";
import AddProductTags from "../../component/AddProductTag";
import Breadcrumbs from "../../component/Breadcrumbs";
import "../../css/productTag.css";
import EmptyState from "../../util/EmptyState";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import SafeImage from "../../component/SafeImage";

const { Title } = Typography;

const ProductTags = () => {
  const [searchText, setSearchText] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addTagModal, setAddTagModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tagDetails, setTagDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(4);
  const [totalTags, setTotalTags] = useState(0);

  // ✅ Fetch Tags
  const fetchTags = async () => {
    try {
      const params = {
        limit: pageSize,
        skip: (page - 1) * pageSize,
        sort: JSON.stringify({ updatedAt: -1 }),
      };
      setLoading(true);
      const response = await ProductTagsServ.index(params);
      const list = Array.isArray(response) ? response : (response?.data || []);
      setTags(list);
      setTotalTags(response?.total || list.length);
    } catch (error) {
      console.error("Error fetching product tags:", error);
      message.error("Failed to fetch product tags");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setPage(page);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const onShowSizeChange = (current, size) => setPageSize(size);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchTags(page);
    }, 1000);
    return () => clearTimeout(handler);
  }, [pageSize, page]);

  // ✅ Add
  const handleAddTag = () => {
    setTagDetails(null);
    setIsEditing(false);
    setAddTagModal(true);
  };

  // ✅ Edit
  const handleEdit = (record) => {
    setTagDetails(record);
    setIsEditing(true);
    setAddTagModal(true);
  };

  // ✅ Delete
  const handleDelete = async (record) => {
    try {
      await ProductTagsServ.remove(record.id);
      message.success("Product tag removed successfully");
      fetchTags();
    } catch (error) {
      console.error("Error deleting tag:", error);
      message.error("Failed to remove tag");
    }
  };

  // ✅ Refresh
  const refreshTags = (isRefresh) => {
    setAddTagModal(false);
    if (isRefresh) fetchTags();
  };

  // ✅ Search filter
  const filteredData = tags.filter(
    (item) =>
      item.tag?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.product?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Product Details",
      dataIndex: "productId",
      key: "product",
      width: "50%",
      render: (productId) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SafeImage
            src={productId?.imageUrl}
            alt={productId?.name}
            variant="thumbnail"
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "14px" }}>{productId?.name || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      width: "25%",
      render: (tag) => <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{tag}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: "120px",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#4776E6", fontSize: "16px" }} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to remove this tag?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Remove">
              <Button type="text" danger icon={<DeleteOutlined style={{ fontSize: "16px" }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  let header = 'Product Tags';
  const topRef = React.useRef(null);

  return (
    <div className="pcu-padding-24" ref={topRef}>
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="tags-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {/* Header Section */}
        <div className="tags-header" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "#fff",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                border: "1px solid #f1f5f9",
              }}
            >
              <TagsOutlined style={{ fontSize: "22px", color: "#4776E6" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Product Tags</h1>
              <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{totalTags} labels assigned to products</p>
            </div>
          </div>
          <Button
            type="primary"
            onClick={handleAddTag}
            style={{
              height: "40px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
              border: "none",
              padding: "0 20px",
              fontSize: "13px",
              fontWeight: 700,
              boxShadow: "0 4px 12px rgba(255, 136, 0, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <PlusOutlined style={{ fontSize: "16px" }} />
            Add Tag
          </Button>
        </div>

        {/* Filters */}
        <div className="filter-section" style={{ marginBottom: "24px", display: "grid", gridTemplateColumns: "1fr auto", gap: "12px", alignItems: "flex-end" }}>
          <div style={{ maxWidth: "400px" }}>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Search Tags</span>
            <div style={{ position: "relative" }}>
              <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
              <Input
                placeholder="Search by tag or product..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ height: "40px", paddingLeft: "36px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
          <ResetButton onClick={() => { setSearchText(""); fetchTags(); }} text="Reset Filters" />
        </div>

        <div className="pcu-border-top pcu-margin-x-n24">
          <DataTable
            data={filteredData}
            columns={columns}
            rowKey="id"
            locale={{
              emptyText: (
                <EmptyState
                  title="No Product Tags"
                  description={searchText ? "No tags match your search." : "No product tags found."}
                  gradient="blue"
                  onAction={!searchText ? handleAddTag : undefined}
                  actionText="Add Tag"
                  icon="inbox"
                />
              )
            }}
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalTags,
              onShowSizeChange: onShowSizeChange,
              onChange: handlePageChange,
            }}
          />
        </div>

        {/* Add/Edit Modal */}
        {addTagModal && (
          <AddProductTags
            open={addTagModal}
            tagDetails={tagDetails}
            isEditing={isEditing}
            refreshTags={refreshTags}
          />
        )}
      </div>
    </div>
  );
};

export default ProductTags;

